import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Alert,
  Menu,
  MenuItem,
  Chip,
  Avatar,
  Stack,
  Divider,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress
} from '@mui/material';
import { 
  AddOutlined as AddIcon,
  AudioFileOutlined as AudioFileIcon,
  Close as CloseIcon,
  PictureAsPdfOutlined as PdfIcon,
  ImageOutlined as ImageIcon,
  DescriptionOutlined as DocumentIcon,
  InsertDriveFileOutlined as FileIcon
} from '@mui/icons-material';
import {
  ChevronDown,
  Search,
  FileText,
  Stethoscope,
  Building2,
  GraduationCap,
  Scale,
  Clock,
  Eye,
  HelpCircle,
  X
} from 'lucide-react';
import {
  PlusCircle,
  Folders,
  Users as UsersPhosphor,
  Check,
  Microphone
} from '@phosphor-icons/react';
import { useHeaderContext } from '@/contexts/HeaderContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import { useToast } from '@/components/Toast';
import { getAllTemplates } from '@/services/templateService';
import { getAllQuestionsForTemplate } from '@/services/questionService';
import { getUserCompany } from '@/services/companyService';
import {
  getCollectionsByCompany,
  processCollectionSessions
} from '@/services/sessionCollectionService';
import { Template } from '@/types/templateTypes';
import { Question } from '@/types/questionTypes';
import { Company } from '@/types/companyTypes';
import { SessionCollection } from '@/types/sessionCollectionTypes';
import { createSessionAndNavigate } from '@/services/sessionNavigationService';
import {
  createTranscriptionSession,
  deleteTranscriptionSession,
  startRecording
} from '@/services/transcriptionSessionService';
import {
  uploadAudioChunk,
  finalizeAudioChunks,
  cancelAudioChunks,
  generateBlockId
} from '@/services/audioChunkUploadService';
import {
  processSessionDocuments,
  uploadGenericDocument
} from '@/services/transcriptionDocumentService';
import { runSequentialUploadBatch } from '@/services/uploadBatch';
import type { CreateTranscriptionSessionClientInput } from '@/types/transcriptionSessionTypes';

type TemplateOption = {
  kind: 'template';
  id: number;
  name: string;
  category: string;
  description?: string;
  questionCount?: number;
};

type CreationMode = 'record' | 'upload';

type UploadFileStatusValue =
  | 'pending'
  | 'creating'
  | 'uploading'
  | 'retrying'
  | 'processing'
  | 'complete'
  | 'failed'
  | 'cancelled';

type UploadFileStatus = {
  id: string;
  name: string;
  size: number;
  progress: number;
  status: UploadFileStatusValue;
  message?: string;
  attempts?: number;
  sessionId?: string;
  documentId?: string;
};

type UploadBatchFile = {
  id: string;
  file: File;
  index: number;
};

type UploadedDocumentToast = {
  id: string;
  name: string;
  size: number;
  sessionId: string;
  isDocumentFile: boolean;
};

// Navigation state interface for upload from collection pages
interface UploadNavigationState {
  preSelectedCollectionId?: string;
  preSelectedCollectionName?: string;
  autoOpenFilePicker?: boolean;
}

interface DashboardCreateSessionPageProps {
  mode?: CreationMode;
}

const MAX_UPLOAD_BYTES = 2 * 1024 * 1024 * 1024; // 2GB
const CHUNK_SIZE_BYTES = 5 * 1024 * 1024; // 5MB chunks

const isAudioOrVideoFile = (fileName: string): boolean => {
  const mediaExtensions = [
    '.mp3',
    '.wav',
    '.m4a',
    '.webm',
    '.mp4',
    '.ogg',
    '.flac',
    '.aac',
    '.mov',
    '.avi',
    '.mkv'
  ];
  return mediaExtensions.some((ext) => fileName.toLowerCase().endsWith(ext));
};

const getFileTitleBase = (fileName: string): string => {
  const trimmed = fileName.trim();
  const extensionIndex = trimmed.lastIndexOf('.');
  if (extensionIndex <= 0) {
    return trimmed;
  }
  return trimmed.slice(0, extensionIndex);
};

const getUploadFileStatusId = (file: File, index: number): string =>
  `${file.name}-${file.size}-${file.lastModified}-${index}`;

const createUploadFileStatuses = (files: File[]): UploadFileStatus[] =>
  files.map((file, index) => ({
    id: getUploadFileStatusId(file, index),
    name: file.name,
    size: file.size,
    progress: 0,
    status: 'pending'
  }));

const isTerminalUploadStatus = (status: UploadFileStatusValue): boolean =>
  status === 'complete' || status === 'failed' || status === 'cancelled';

const calculateUploadProgress = (statuses: UploadFileStatus[]): number => {
  if (statuses.length === 0) {
    return 0;
  }

  const total = statuses.reduce((sum, fileStatus) => {
    return (
      sum +
      (isTerminalUploadStatus(fileStatus.status) ? 100 : fileStatus.progress)
    );
  }, 0);

  return Math.round(total / statuses.length);
};

const getUploadStatusLabel = (status: UploadFileStatusValue): string => {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'creating':
      return 'Creating session';
    case 'uploading':
      return 'Uploading';
    case 'retrying':
      return 'Retrying';
    case 'processing':
      return 'Finalizing';
    case 'complete':
      return 'Uploaded';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Pending';
  }
};

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : 'File upload failed';

// Category configuration for better visual design
const getCategoryConfig = (category: string) => {
  const configs = {
    Medical: {
      icon: Stethoscope,
      color: '#10b981', // emerald-500
      bgColor: '#ecfdf5', // emerald-50
      textColor: '#047857' // emerald-700
    },
    Business: {
      icon: Building2,
      color: '#3b82f6', // blue-500
      bgColor: '#eff6ff', // blue-50
      textColor: '#1d4ed8' // blue-700
    },
    Education: {
      icon: GraduationCap,
      color: '#8b5cf6', // violet-500
      bgColor: '#f5f3ff', // violet-50
      textColor: '#6d28d9' // violet-700
    },
    Legal: {
      icon: Scale,
      color: '#f59e0b', // amber-500
      bgColor: '#fffbeb', // amber-50
      textColor: '#d97706' // amber-700
    },
    General: {
      icon: FileText,
      color: '#6b7280', // gray-500
      bgColor: '#f9fafb', // gray-50
      textColor: '#374151' // gray-700
    }
  };

  return configs[category as keyof typeof configs] || configs.General;
};

const DashboardCreateSessionPage: React.FC<DashboardCreateSessionPageProps> = ({ mode }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser } = useAppUser();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const hasAutoOpenedRef = useRef(false);
  
  // Get navigation state for upload from collection pages
  const navigationState = location.state as UploadNavigationState | undefined;
  const preSelectedCollectionId = navigationState?.preSelectedCollectionId;
  const preSelectedCollectionName = navigationState?.preSelectedCollectionName;
  const autoOpenFilePicker = navigationState?.autoOpenFilePicker;

  const [templates, setTemplates] = useState<Template[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [sessionTitle, setSessionTitle] = useState<string>('');
  const [selectedOption, setSelectedOption] = useState<TemplateOption | null>(
    null
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [creating, setCreating] = useState<boolean>(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);
  const [previewTemplateId, setPreviewTemplateId] = useState<number | null>(
    null
  );
  const [previewQuestions, setPreviewQuestions] = useState<Question[]>([]);
  const [loadingPreview, setLoadingPreview] = useState<boolean>(false);

  // Collection-related state
  const [collections, setCollections] = useState<SessionCollection[]>([]);
  const [collectionInput, setCollectionInput] = useState<string>('');
  const [selectedCollectionId, setSelectedCollectionId] = useState<
    string | null
  >(null); // Track selected collection ID
  const [collectionMenuAnchor, setCollectionMenuAnchor] =
    useState<null | HTMLElement>(null);
  const collectionMenuOpen = Boolean(collectionMenuAnchor);
  const [collectionSearchTerm, setCollectionSearchTerm] = useState<string>('');
  // Use mode prop if provided, otherwise default to 'record'
  const [creationMode, setCreationMode] = useState<CreationMode>(mode || 'record');
  const [selectedAudioFiles, setSelectedAudioFiles] = useState<File[]>([]);
  const [fileValidationError, setFileValidationError] = useState<string | null>(
    null
  );
  const [uploadedDocuments, setUploadedDocuments] = useState<Array<{ id: string; name: string; size: number }>>([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [showAllFiles, setShowAllFiles] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatusText, setUploadStatusText] = useState('Preparing upload…');
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadFileStatuses, setUploadFileStatuses] = useState<UploadFileStatus[]>([]);
  const uploadFileStatusesRef = useRef<UploadFileStatus[]>([]);
  const uploadCancelRef = useRef(false);
  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  const [summaryType, setSummaryType] = useState<'individual' | 'combined'>('individual');

  const setUploadFileStatusSnapshot = (statuses: UploadFileStatus[]) => {
    uploadFileStatusesRef.current = statuses;
    setUploadFileStatuses(statuses);
    setUploadProgress(calculateUploadProgress(statuses));
  };

  const updateUploadFileStatus = (
    id: string,
    patch: Partial<UploadFileStatus>
  ) => {
    const nextStatuses = uploadFileStatusesRef.current.map((fileStatus) =>
      fileStatus.id === id ? { ...fileStatus, ...patch } : fileStatus
    );
    setUploadFileStatusSnapshot(nextStatuses);
  };

  const markPendingUploadsCancelled = () => {
    const nextStatuses = uploadFileStatusesRef.current.map((fileStatus) =>
      isTerminalUploadStatus(fileStatus.status)
        ? fileStatus
        : {
            ...fileStatus,
            status: 'cancelled' as const,
            message: 'Upload cancelled'
          }
    );
    setUploadFileStatusSnapshot(nextStatuses);
  };

  // Sync creationMode with mode prop when it changes
  useEffect(() => {
    if (mode) {
      setCreationMode(mode);
    }
  }, [mode]);

  useEffect(() => {
    const headerTitle = mode === 'upload' 
      ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <PlusCircle size={16} weight="regular" />
            <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
              Upload
            </Typography>
          </Box>
        )
      : mode === 'record' 
        ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Microphone size={16} weight="regular" />
              <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                Record
              </Typography>
            </Box>
          )
        : 'Create';
    setHeaderText(headerTitle);
    setMenuActions(null);

    return () => {
      setHeaderText('');
      setMenuActions(null);
    };
  }, [setHeaderText, setMenuActions, mode]);

  useEffect(() => {
    const run = async () => {
      if (!appUser?.id) return;
      if (!getAccessTokenSilently || !getAccessTokenWithPopup) return;
      setLoading(true);
      setError(null);
      try {
        // Fetch templates, company data, and collections in parallel
        const [templatesResult, companyResult] = await Promise.all([
          getAllTemplates(
            { userId: appUser.id, includeArchived: false },
            getAccessTokenSilently,
            getAccessTokenWithPopup
          ),
          getUserCompany(
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          )
        ]);

        setTemplates(templatesResult);
        setCompany(companyResult);

        // Fetch collections once we have company data
        if (companyResult) {
          const collectionsResult = await getCollectionsByCompany(
            companyResult.id,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup,
            1,
            100
          );
          setCollections(collectionsResult);
        }
      } catch (e: any) {
        setError(e?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [appUser?.id, getAccessTokenSilently, getAccessTokenWithPopup]);

  // Set default template when both templates and company data are loaded
  useEffect(() => {
    if (templates.length > 0 && company && company.defaultTemplateId) {
      // Find the default template in the loaded templates
      const defaultTemplate = templates.find(
        (t) => t.id === company.defaultTemplateId
      );
      if (defaultTemplate) {
        setSelectedOption({
          kind: 'template',
          id: defaultTemplate.id,
          name: defaultTemplate.name,
          category: defaultTemplate.category?.name || 'General',
          description:
            defaultTemplate.description || 'No description available',
          questionCount: defaultTemplate.questions?.length || 0
        });
      }
    }
  }, [templates, company]);

  // Pre-select collection when navigating from a collection page
  useEffect(() => {
    if (preSelectedCollectionId && preSelectedCollectionName) {
      setCollectionInput(preSelectedCollectionName);
      setSelectedCollectionId(preSelectedCollectionId);
      // Clear navigation state to prevent re-triggering
      navigate(location.pathname, { replace: true, state: undefined });
    }
  }, [preSelectedCollectionId, preSelectedCollectionName, navigate, location.pathname]);

  // Auto-open file picker when navigating from a collection page with autoOpenFilePicker
  useEffect(() => {
    if (autoOpenFilePicker && !hasAutoOpenedRef.current && fileInputRef.current && mode === 'upload') {
      hasAutoOpenedRef.current = true;
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        fileInputRef.current?.click();
      }, 300);
    }
  }, [autoOpenFilePicker, mode]);

  const options: TemplateOption[] = useMemo(() => {
    return templates.map<TemplateOption>((t) => ({
      kind: 'template',
      id: t.id,
      name: t.name,
      category: t.category?.name || 'General',
      description: t.description || 'No description available',
      questionCount: t.questions?.length || 0
    }));
  }, [templates]);

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return options;
    return options.filter((o) => o.name.toLowerCase().includes(term));
  }, [options, searchTerm]);

  const filteredCollections = useMemo(() => {
    const term = collectionSearchTerm.trim().toLowerCase();
    if (!term) return collections;
    return collections.filter((c) => c.title.toLowerCase().includes(term));
  }, [collections, collectionSearchTerm]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSearchTerm(''); // Clear search when closing
  };

  const handleOptionSelect = (option: TemplateOption) => {
    setSelectedOption(option);
    handleMenuClose();
  };

  const handleCollectionMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setCollectionMenuAnchor(event.currentTarget);
  };

  const handleCollectionMenuClose = () => {
    setCollectionMenuAnchor(null);
    setCollectionSearchTerm('');
  };

  const handleCollectionInputChange = (
    value: string,
    collectionId?: string
  ) => {
    setCollectionInput(value);
    setSelectedCollectionId(collectionId || null); // Set ID if existing collection, null if new
    handleCollectionMenuClose();
  };

  const handleClearSelectedAudio = (index?: number) => {
    if (index !== undefined) {
      // Remove specific file
      setSelectedAudioFiles(prev => prev.filter((_, i) => i !== index));
    } else {
      // Clear all files
      setSelectedAudioFiles([]);
      setShowAllFiles(false);
    }
    setFileValidationError(null);
    setUploadError(null);
    if (!isUploadingFile) {
      setUploadFileStatusSnapshot([]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddMoreFiles = (newFiles: File[]) => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    newFiles.forEach((file) => {
      if (file.size > MAX_UPLOAD_BYTES) {
        errors.push(`${file.name} is too large (must be 2GB or smaller)`);
      } else {
        validFiles.push(file);
      }
    });

    if (errors.length > 0) {
      setFileValidationError(errors.join(', '));
    } else {
      setFileValidationError(null);
      setSelectedAudioFiles(prev => [...prev, ...validFiles]);
    }
  };

  const handleCreationModeChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: CreationMode | null
  ) => {
    if (!newMode) return;
    // If mode prop is provided, navigate to the appropriate route instead of changing state
    if (mode) {
      navigate(newMode === 'record' ? '/create/record' : '/create/upload');
      return;
    }
    setCreationMode(newMode);
    if (newMode === 'record') {
      handleClearSelectedAudio();
    }
  };

  const handleAudioUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleAudioFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      return;
    }
    handleAddMoreFiles(files);
    // Reset input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleAddMoreFiles(files);
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.toLowerCase().split('.').pop();
    const iconColor = theme.palette.text.secondary;
    const iconSize = 20;
    
    if (['mp3', 'wav', 'm4a', 'ogg', 'flac', 'aac', 'wma'].includes(ext || '')) {
      return <AudioFileIcon sx={{ color: iconColor, fontSize: iconSize }} />;
    }
    if (ext === 'pdf') {
      return <PdfIcon sx={{ color: iconColor, fontSize: iconSize }} />;
    }
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext || '')) {
      return <ImageIcon sx={{ color: iconColor, fontSize: iconSize }} />;
    }
    if (['doc', 'docx', 'txt', 'rtf'].includes(ext || '')) {
      return <DocumentIcon sx={{ color: iconColor, fontSize: iconSize }} />;
    }
    return <FileIcon sx={{ color: iconColor, fontSize: iconSize }} />;
  };

  // Check if collection input matches an existing collection
  const isNewCollection = useMemo(() => {
    if (!collectionInput.trim()) return false;
    const trimmed = collectionInput.trim().toLowerCase();
    return !collections.some((c) => c.title.toLowerCase() === trimmed);
  }, [collectionInput, collections]);

  const handlePreviewTemplate = async (
    templateId: number,
    event: React.MouseEvent
  ) => {
    event.stopPropagation(); // Prevent card selection
    setPreviewTemplateId(templateId);
    setLoadingPreview(true);
    setPreviewQuestions([]);

    if (!appUser?.id) {
      setLoadingPreview(false);
      return;
    }

    try {
      const questions = await getAllQuestionsForTemplate(
        templateId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      setPreviewQuestions(questions);
    } catch (error) {
      console.error('Failed to load template questions:', error);
      setPreviewQuestions([]);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewTemplateId(null);
  };

  const isPrimaryActionDisabled =
    creating ||
    loading ||
    !selectedOption ||
    isUploadingFile ||
    (creationMode === 'upload' && selectedAudioFiles.length === 0);

  const primaryActionLabel =
    creationMode === 'upload'
      ? isUploadingFile
        ? 'Uploading…'
        : 'Upload'
      : creating
      ? 'Creating…'
      : 'Record';

  const onCreate = async () => {
    if (!appUser?.id) return;
    if (!getAccessTokenSilently || !getAccessTokenWithPopup) return;
    if (!selectedOption) return; // Should not happen since we always have a default template

    const templateId = selectedOption.id;
    const templateName = selectedOption.name;
    setCreating(true);
    try {
      // Use provided title if present; otherwise service will generate a default one
      const createdId = await createSessionAndNavigate({
        templateId,
        templateName,
        explicitTitle: sessionTitle,
        userId: appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        navigate,
        // Pass collection ID if existing collection selected, otherwise pass name for new collection
        collectionId: selectedCollectionId || undefined,
        collectionName:
          !selectedCollectionId && collectionInput.trim()
            ? collectionInput.trim()
            : undefined
      });
      return createdId;
    } finally {
      setCreating(false);
    }
  };

  const handleUploadFlow = async () => {
    if (!appUser?.id) return;
    if (!getAccessTokenSilently || !getAccessTokenWithPopup) return;
    if (!selectedOption) {
      setFileValidationError('Select a template before uploading.');
      return;
    }
    if (selectedAudioFiles.length === 0) {
      setFileValidationError('Please attach at least one file to continue.');
      return;
    }

    const filesToUpload = [...selectedAudioFiles];
    const uploadItems: UploadBatchFile[] = filesToUpload.map((file, index) => ({
      id: getUploadFileStatusId(file, index),
      file,
      index
    }));

    setFileValidationError(null);
    setUploadError(null);
    setUploadedDocuments([]);
    setUploadDialogOpen(true);
    setIsUploadingFile(true);
    setUploadStatusText('Preparing upload…');
    setUploadFileStatusSnapshot(createUploadFileStatuses(filesToUpload));
    uploadCancelRef.current = false;
    uploadAbortControllerRef.current = new AbortController();

    const baseSessionTitle = sessionTitle?.trim()
      ? sessionTitle.trim()
      : `${selectedOption.name} Session - ${new Date().toLocaleString()}`;
    const sharedCollectionName =
      !selectedCollectionId && collectionInput.trim()
        ? collectionInput.trim()
        : null;
    const shouldSplitIntoSeparateSessions =
      summaryType === 'individual' && filesToUpload.length > 1;

    let createdSessionId: string | undefined;
    let createdSessionIds: string[] = [];
    let resolvedCollectionId: string | null = selectedCollectionId;
    let shouldCloseDialog = false;
    let uploadedDocumentsForToast: UploadedDocumentToast[] = [];
    let failedFiles: File[] = [];
    let uploadStopped = false;

    try {
      if (shouldSplitIntoSeparateSessions) {
        const totalFiles = filesToUpload.length;

        const uploadResult = await runSequentialUploadBatch<
          UploadBatchFile,
          UploadedDocumentToast
        >(
          uploadItems,
          async (item, _index, attempt) => {
            const { file, id, index } = item;
            const fileSessionTitle = sessionTitle?.trim()
              ? `${sessionTitle.trim()} - ${getFileTitleBase(file.name)}`
              : getFileTitleBase(file.name);
            const attemptSuffix = attempt > 1 ? ` (attempt ${attempt})` : '';
            let createdSession: Awaited<
              ReturnType<typeof createTranscriptionSession>
            > | null = null;
            let uploadCompleted = false;

            try {
              if (uploadCancelRef.current) {
                throw new Error('Upload cancelled');
              }

              setUploadStatusText(
                `Creating session ${index + 1} of ${totalFiles}: ${file.name}${attemptSuffix}…`
              );
              updateUploadFileStatus(id, {
                status: attempt > 1 ? 'retrying' : 'creating',
                progress: 2,
                attempts: attempt,
                message: undefined
              });

              const collectionIdForSession =
                resolvedCollectionId || selectedCollectionId || undefined;
              createdSession = await createTranscriptionSession(
                {
                  title: fileSessionTitle,
                  templateId: selectedOption.id,
                  sessionStartTime: new Date().toISOString(),
                  collectionId: collectionIdForSession,
                  collectionName: collectionIdForSession
                    ? undefined
                    : sharedCollectionName
                },
                appUser.id,
                getAccessTokenSilently,
                getAccessTokenWithPopup
              );

              if (!resolvedCollectionId && createdSession.collections?.length) {
                resolvedCollectionId = createdSession.collections[0].id;
              }

              setUploadStatusText(
                `Uploading ${index + 1} of ${totalFiles}: ${file.name}${attemptSuffix}…`
              );
              updateUploadFileStatus(id, {
                status: 'uploading',
                progress: 5,
                sessionId: createdSession.id,
                attempts: attempt
              });

              const uploadResult = await uploadGenericDocument(
                createdSession.id,
                file,
                appUser.id,
                getAccessTokenSilently,
                getAccessTokenWithPopup,
                {
                  skipAnalysis: !isAudioOrVideoFile(file.name),
                  abortSignal: uploadAbortControllerRef.current?.signal,
                  onProgress: (filePercent) => {
                    updateUploadFileStatus(id, {
                      status: 'uploading',
                      progress: Math.min(filePercent, 99),
                      attempts: attempt
                    });
                  }
                }
              );

              if (!uploadResult.success || !uploadResult.document) {
                throw new Error(
                  `Failed to upload ${file.name}: ${uploadResult.message || 'Unknown error'}`
                );
              }
              uploadCompleted = true;

              updateUploadFileStatus(id, {
                status: 'processing',
                progress: 99,
                documentId: uploadResult.document.id
              });

              const uploadedDocument = {
                id: uploadResult.document.id,
                name: file.name,
                size: file.size,
                sessionId: createdSession.id,
                isDocumentFile: !isAudioOrVideoFile(file.name)
              };

              updateUploadFileStatus(id, {
                status: 'complete',
                progress: 100,
                documentId: uploadResult.document.id,
                message: 'Upload complete'
              });

              return uploadedDocument;
            } catch (error) {
              let message = uploadCancelRef.current
                ? 'Upload cancelled'
                : getErrorMessage(error);

              if (createdSession?.id && !uploadCompleted) {
                const cleanupStatus = uploadCancelRef.current
                  ? 'cancelled'
                  : 'failed';
                setUploadStatusText(
                  `Cleaning up ${cleanupStatus} session for ${file.name}…`
                );
                updateUploadFileStatus(id, {
                  status: cleanupStatus,
                  message: `${message}. Cleaning up ${cleanupStatus} session…`,
                  attempts: attempt
                });

                try {
                  await deleteTranscriptionSession(
                    createdSession.id,
                    appUser.id,
                    getAccessTokenSilently,
                    getAccessTokenWithPopup
                  );
                } catch (cleanupError) {
                  console.error(
                    `Failed to delete upload session ${createdSession.id}:`,
                    cleanupError
                  );
                  message = `${message}. Failed to delete the session: ${getErrorMessage(
                    cleanupError
                  )}`;
                }
              }

              updateUploadFileStatus(id, {
                status: uploadCancelRef.current ? 'cancelled' : 'failed',
                message,
                attempts: attempt
              });

              throw new Error(message);
            }
          },
          {
            maxAttempts: 2,
            retryDelayMs: 750,
            shouldStop: () => uploadCancelRef.current,
            onRetry: (item, _index, attempt, error) => {
              updateUploadFileStatus(item.id, {
                status: 'retrying',
                progress: 0,
                message: `Attempt ${attempt} failed: ${error.message}`,
                attempts: attempt + 1
              });
              setUploadStatusText(`Retrying ${item.file.name}…`);
            }
          }
        );

        uploadStopped = uploadResult.stopped;
        uploadedDocumentsForToast = uploadResult.successes.map(
          (success) => success.result
        );
        failedFiles = uploadResult.failures.map((failure) => failure.item.file);
        createdSessionIds = uploadedDocumentsForToast.map((doc) => doc.sessionId);
        createdSessionId = createdSessionIds[0];

        const documentSessionIds = uploadedDocumentsForToast
          .filter((doc) => doc.isDocumentFile)
          .map((doc) => doc.sessionId);

        if (!uploadStopped && documentSessionIds.length > 1 && resolvedCollectionId) {
          setUploadStatusText('Starting folder document processing…');
          await processCollectionSessions(
            resolvedCollectionId,
            documentSessionIds,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
        } else if (!uploadStopped && documentSessionIds.length === 1) {
          setUploadStatusText('Starting document processing…');
          await processSessionDocuments(
            documentSessionIds[0],
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
        } else if (!uploadStopped && documentSessionIds.length > 1) {
          for (const sessionId of documentSessionIds) {
            setUploadStatusText('Starting document processing…');
            await processSessionDocuments(
              sessionId,
              appUser.id,
              getAccessTokenSilently,
              getAccessTokenWithPopup
            );
          }
        }
      } else {
        setUploadStatusText('Creating session…');
        const createdSession = await createTranscriptionSession(
          {
            title: baseSessionTitle,
            templateId: selectedOption.id,
            sessionStartTime: new Date().toISOString(),
            collectionId: selectedCollectionId,
            collectionName: sharedCollectionName
          },
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
        const sessionId = createdSession.id;
        createdSessionId = sessionId;
        createdSessionIds = [sessionId];
        if (!resolvedCollectionId && createdSession.collections?.length) {
          resolvedCollectionId = createdSession.collections[0].id;
        }

        const totalFiles = filesToUpload.length;

        const uploadResult = await runSequentialUploadBatch<
          UploadBatchFile,
          UploadedDocumentToast
        >(
          uploadItems,
          async (item, _index, attempt) => {
            const { file, id, index } = item;
            const attemptSuffix = attempt > 1 ? ` (attempt ${attempt})` : '';

            try {
              if (uploadCancelRef.current) {
                throw new Error('Upload cancelled');
              }

              setUploadStatusText(
                `Uploading ${index + 1} of ${totalFiles}: ${file.name}${attemptSuffix}…`
              );
              updateUploadFileStatus(id, {
                status: attempt > 1 ? 'retrying' : 'uploading',
                progress: 0,
                sessionId,
                attempts: attempt,
                message: undefined
              });

              const uploadResult = await uploadGenericDocument(
                sessionId,
                file,
                appUser.id,
                getAccessTokenSilently,
                getAccessTokenWithPopup,
                {
                  skipAnalysis: !isAudioOrVideoFile(file.name),
                  abortSignal: uploadAbortControllerRef.current?.signal,
                  onProgress: (filePercent) => {
                    updateUploadFileStatus(id, {
                      status: 'uploading',
                      progress: Math.min(filePercent, 99),
                      attempts: attempt
                    });
                  }
                }
              );

              if (!uploadResult.success || !uploadResult.document) {
                throw new Error(
                  `Failed to upload ${file.name}: ${uploadResult.message || 'Unknown error'}`
                );
              }

              updateUploadFileStatus(id, {
                status: 'complete',
                progress: 100,
                documentId: uploadResult.document.id,
                message: 'Upload complete'
              });

              return {
                id: uploadResult.document.id,
                name: file.name,
                size: file.size,
                sessionId,
                isDocumentFile: !isAudioOrVideoFile(file.name)
              };
            } catch (error) {
              const message = uploadCancelRef.current
                ? 'Upload cancelled'
                : getErrorMessage(error);
              updateUploadFileStatus(id, {
                status: uploadCancelRef.current ? 'cancelled' : 'failed',
                message,
                attempts: attempt
              });
              throw new Error(message);
            }
          },
          {
            maxAttempts: 2,
            retryDelayMs: 750,
            shouldStop: () => uploadCancelRef.current,
            onRetry: (item, _index, attempt, error) => {
              updateUploadFileStatus(item.id, {
                status: 'retrying',
                progress: 0,
                message: `Attempt ${attempt} failed: ${error.message}`,
                attempts: attempt + 1
              });
              setUploadStatusText(`Retrying ${item.file.name}…`);
            }
          }
        );

        uploadStopped = uploadResult.stopped;
        uploadedDocumentsForToast = uploadResult.successes.map(
          (success) => success.result
        );
        failedFiles = uploadResult.failures.map((failure) => failure.item.file);

        const uploadedSessionDocuments = uploadedDocumentsForToast.filter(
          (doc) => doc.isDocumentFile
        ).length;

        if (!uploadStopped && uploadedSessionDocuments > 0) {
          setUploadStatusText('Starting document processing…');
          await processSessionDocuments(
            sessionId,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
        }

        if (
          uploadedDocumentsForToast.length === 0 &&
          (failedFiles.length > 0 || uploadStopped) &&
          sessionId
        ) {
          setUploadStatusText(
            uploadStopped
              ? 'Cleaning up cancelled session…'
              : 'Cleaning up failed session…'
          );
          await deleteTranscriptionSession(
            sessionId,
            appUser.id,
            getAccessTokenSilently,
            getAccessTokenWithPopup
          );
        }
      }

      setUploadedDocuments(
        uploadedDocumentsForToast.map((doc) => ({
          id: doc.id,
          name: doc.name,
          size: doc.size
        }))
      );

      if (uploadStopped) {
        markPendingUploadsCancelled();
        setUploadStatusText('Upload cancelled');
        setUploadError(
          uploadedDocumentsForToast.length > 0
            ? `${uploadedDocumentsForToast.length} file${
                uploadedDocumentsForToast.length === 1 ? '' : 's'
              } uploaded before cancellation.`
            : 'Upload cancelled.'
        );
        return;
      }

      const uploadedCount = uploadedDocumentsForToast.length;
      const failedCount = failedFiles.length;
      const failedFileNoun = failedCount === 1 ? 'file' : 'files';

      if (uploadedCount === 0 && failedCount > 0) {
        const firstFailure = uploadFileStatusesRef.current.find(
          (status) => status.status === 'failed'
        );
        const message = firstFailure?.message
          ? `No files uploaded. ${firstFailure.name}: ${firstFailure.message}`
          : 'No files uploaded. Please try again.';
        setUploadStatusText('Upload failed');
        setUploadError(message);
        setSelectedAudioFiles(failedFiles);
        toast.showError(message);
        return;
      }

      if (failedCount > 0) {
        setUploadStatusText('Upload partially complete');
        setUploadError(
          `${uploadedCount} file${uploadedCount === 1 ? '' : 's'} uploaded. ${failedCount} ${failedFileNoun} failed and remain selected for retry.`
        );
        setSelectedAudioFiles(failedFiles);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

        toast.showWarning(
          `${uploadedCount} uploaded. ${failedCount} ${failedFileNoun} failed.`,
          {
            duration: 10000,
            actions: [
              {
                label: resolvedCollectionId ? 'View Folder' : 'View Uploaded',
                onClick: () =>
                  navigate(
                    resolvedCollectionId
                      ? `/files/folder/${resolvedCollectionId}`
                      : createdSessionId
                      ? `/transcript/${createdSessionId}`
                      : '/files'
                  ),
                variant: 'primary'
              }
            ]
          }
        );
        return;
      }

      setUploadStatusText('Upload complete!');
      setUploadProgress(100);

      shouldCloseDialog = true;
      handleClearSelectedAudio();

      const fileCount = filesToUpload.length;
      const fileNoun = fileCount === 1 ? 'file' : 'files';

      if (shouldSplitIntoSeparateSessions) {
        const sessionCount = createdSessionIds.length;
        toast.showSuccess(
          `${fileCount} ${fileNoun} uploaded into ${sessionCount} sessions.`,
          {
            duration: 10000,
            actions: [
              {
                label: resolvedCollectionId ? 'View Folder' : 'View Files',
                onClick: () =>
                  navigate(
                    resolvedCollectionId
                      ? `/files/folder/${resolvedCollectionId}`
                      : '/files'
                  ),
                variant: 'primary'
              },
              {
                label: 'View First Session',
                onClick: () => {
                  if (createdSessionId) {
                    navigate(`/transcript/${createdSessionId}`);
                  }
                },
                variant: 'secondary'
              }
            ]
          }
        );
      } else {
        toast.showSuccess(`${fileCount} ${fileNoun} uploaded successfully!`, {
          duration: 10000,
          actions: [
            {
              label: 'View File',
              onClick: () => {
                if (createdSessionId) {
                  navigate(`/transcript/${createdSessionId}`);
                }
              },
              variant: 'primary'
            },
            {
              label: 'Ask AI',
              onClick: () => {
                if (createdSessionId) {
                  navigate(`/transcript/${createdSessionId}`, {
                    state: { openChat: true }
                  });
                }
              },
              variant: 'secondary'
            },
            {
              label: 'Add to Folder',
              onClick: () => {
                if (createdSessionId) {
                  navigate(`/transcript/${createdSessionId}`);
                }
              },
              variant: 'secondary'
            }
          ]
        });
      }
    } catch (error) {
      const message = getErrorMessage(error);
      setUploadError(message);
      setUploadStatusText('Upload failed');
    } finally {
      uploadAbortControllerRef.current = null;
      uploadCancelRef.current = false;
      setIsUploadingFile(false);
      if (shouldCloseDialog) {
        setUploadDialogOpen(false);
      }
    }
  };

  const handleCancelUpload = () => {
    if (!isUploadingFile) {
      setUploadDialogOpen(false);
      setUploadError(null);
      return;
    }
    uploadCancelRef.current = true;
    uploadAbortControllerRef.current?.abort();
    setUploadStatusText('Cancelling upload…');
  };

  const handleUploadDialogClose = () => {
    if (isUploadingFile) return;
    setUploadDialogOpen(false);
    setUploadError(null);
    setUploadStatusText('Preparing upload…');
    setUploadProgress(0);
    setUploadFileStatusSnapshot([]);
  };

  const handlePrimaryAction = async () => {
    if (creationMode === 'upload') {
      await handleUploadFlow();
      return;
    }

    return onCreate();
  };

  return (
    <Box
      data-onboarding="create-page"
      sx={{
        minHeight: isMobile ? 'calc(100vh - 140px)' : '100vh', // Account for header + bottom nav on mobile
        display: 'flex',
        alignItems: isMobile ? 'center' : 'flex-start', // Center vertically on mobile
        justifyContent: 'center',
        pt: isMobile ? 2 : '4vh', // Less top padding on mobile, reduced on desktop for better centering
        px: isMobile ? 1.5 : 3,
        pb: isMobile ? 2 : 3 // Less bottom padding on mobile
      }}
    >
      <Box
        sx={{
          maxWidth: isMobile ? '100%' : 560, // Full width on mobile
          width: '100%'
        }}
      >
        <Card
          elevation={0}
          sx={{
            borderRadius: '6px',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper
          }}
        >
          <CardContent sx={{ p: isMobile ? 2 : 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Drag and drop file upload - shown at top when in upload mode */}
            {creationMode === 'upload' && (
              <Box sx={{ mb: 2 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={handleAudioFileChange}
                  aria-label="Upload files"
                />
                
                {/* Drag and drop area */}
                <Box
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onClick={handleAudioUploadClick}
                  sx={{
                    border: `2px dashed ${theme.palette.divider}`,
                    borderRadius: '10px',
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.02)'
                        : '#f9fafb',
                    px: 2,
                    py: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      backgroundColor:
                        theme.palette.mode === 'dark'
                          ? 'rgba(59, 130, 246, 0.08)'
                          : '#eff6ff'
                    }
                  }}
                >
                  <PlusCircle
                    size={isMobile ? 32 : 40}
                    weight="regular"
                    style={{ 
                      marginBottom: 12,
                      color: theme.palette.text.secondary
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    {selectedAudioFiles.length > 0
                      ? 'Add more files or click to browse'
                      : 'Drag & drop files here or click to browse'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    PDF, images, audio, documents, and more up to 2GB each
                  </Typography>
                </Box>

                {fileValidationError && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {fileValidationError}
                  </Alert>
                )}
              </Box>
            )}

            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: '1fr',
                mb: 2
              }}
            >
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0.75,
                    fontWeight: 400,
                    textAlign: 'left',
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                    fontSize: '13px'
                  }}
                >
                  Title
                </Typography>
                <TextField
                  placeholder="Enter a title or leave blank to auto-generate"
                  value={sessionTitle}
                  onChange={(e) => setSessionTitle(e.target.value)}
                  variant="outlined"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#fff',
                      '& input': {
                        py: 1,
                        px: 1.5
                      },
                      '& fieldset': {
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                        borderWidth: '1px'
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                        borderWidth: '1px'
                      }
                    },
                    '& .MuiOutlinedInput-input': {
                      color: theme.palette.text.primary
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                      color: theme.palette.text.secondary,
                      opacity: 0.6
                    }
                  }}
                />
              </Box>

              {/* Template Picker Button */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0.75,
                    fontWeight: 400,
                    textAlign: 'left',
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                    fontSize: '13px'
                  }}
                >
                  Template
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleMenuOpen}
                  disabled={loading}
                  endIcon={
                    loading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )
                  }
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    borderRadius: '8px',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#fff',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                    color: theme.palette.text.primary,
                    py: 1,
                    px: 1.5,
                    '&:hover': {
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
                    }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                      color: selectedOption ? theme.palette.text.primary : theme.palette.text.secondary,
                      flex: 1,
                      textAlign: 'left'
                    }}
                  >
                    {selectedOption ? (
                      <>
                        {selectedOption.name}
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            fontSize: '12px',
                            color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)'
                          }}
                        >
                          {selectedOption.category}
                        </Typography>
                      </>
                    ) : (
                      'Select template...'
                    )}
                  </Typography>
                </Button>

                {/* Template Menu */}
                <Menu
                  anchorEl={menuAnchor}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  PaperProps={{
                    sx: {
                      maxHeight: isMobile ? '60vh' : 440,
                      width: isMobile
                        ? '95vw'
                        : menuAnchor
                        ? `${menuAnchor.offsetWidth}px`
                        : '480px',
                      mt: 0.5,
                      borderRadius: '12px',
                      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 10px 40px -10px rgba(0, 0, 0, 0.6), 0 0 1px rgba(255, 255, 255, 0.1)'
                        : '0 10px 40px -10px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.05)',
                      overflow: 'hidden'
                    }
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                >
                  {/* Search box - Sticky */}
                  <Box
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      backgroundColor: theme.palette.background.paper,
                      px: 2,
                      pt: 2,
                      pb: 1.5,
                      borderBottom: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <TextField
                      placeholder="Search templates..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      autoFocus
                      InputProps={{
                        startAdornment: (
                          <Search
                            size={18}
                            style={{
                              marginRight: 10,
                              color: theme.palette.text.disabled
                            }}
                          />
                        )
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                          fontSize: '14px',
                          transition: 'all 0.15s ease',
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)'
                          },
                          '&.Mui-focused': {
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.primary.main,
                            boxShadow: `0 0 0 3px ${theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)'}`
                          }
                        },
                        '& .MuiOutlinedInput-input': {
                          fontSize: '14px',
                          py: 1,
                          px: 1.5,
                          '&::placeholder': {
                            color: theme.palette.text.disabled,
                            opacity: 1
                          }
                        }
                      }}
                    />
                  </Box>

                  {/* Options - Scrollable */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      maxHeight: isMobile ? 'calc(60vh - 120px)' : 320,
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px'
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent'
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                        borderRadius: '4px',
                        '&:hover': {
                          background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'
                        }
                      }
                    }}
                  >
                    {filteredOptions.length > 0 ? (
                      <Stack spacing={0.5}>
                        {filteredOptions.map((option, index) => {
                          const isSelected = selectedOption?.id === option.id;

                          const config = getCategoryConfig(option.category);
                          const IconComponent = config.icon;

                          return (
                            <Box
                              key={`${option.kind}-${option.id ?? 'none'}`}
                              onClick={() => handleOptionSelect(option)}
                              sx={{
                                cursor: 'pointer',
                                backgroundColor: 'transparent',
                                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                borderRadius: '8px',
                                p: 1.5,
                                mb: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: config.bgColor,
                                  color: config.color,
                                  borderRadius: '6px',
                                  flexShrink: 0
                                }}
                              >
                                <IconComponent size={16} />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 0.5
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 500,
                                      fontSize: '14px',
                                      color: theme.palette.text.primary
                                    }}
                                  >
                                    {option.name}
                                  </Typography>
                                  <Chip
                                    label={option.category}
                                    size="small"
                                    sx={{
                                      fontSize: '11px',
                                      height: 18,
                                      bgcolor: config.bgColor,
                                      color: config.textColor,
                                      border: 'none'
                                    }}
                                  />
                                </Box>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    fontSize: '12px'
                                  }}
                                >
                                  {option.questionCount} question{option.questionCount !== 1 ? 's' : ''}
                                </Typography>
                              </Box>
                              {isSelected && (
                                <Check
                                  size={18}
                                  weight="bold"
                                  color={theme.palette.text.primary}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    ) : (
                      <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Search
                          size={32}
                          style={{
                            color: theme.palette.text.secondary,
                            marginBottom: 8
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          No templates found
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Try adjusting your search terms
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Menu>
              </Box>

              {/* Collection Picker */}
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0.75,
                    fontWeight: 400,
                    textAlign: 'left',
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                    fontSize: '13px'
                  }}
                >
                  Folders
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleCollectionMenuOpen}
                  disabled={loading}
                  endIcon={
                    loading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    )
                  }
                  sx={{
                    width: '100%',
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    borderRadius: '8px',
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#fff',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                    color: theme.palette.text.primary,
                    py: 1,
                    px: 1.5,
                    '&:hover': {
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                      backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
                    }
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 400,
                      color: collectionInput ? theme.palette.text.primary : theme.palette.text.secondary,
                      flex: 1,
                      textAlign: 'left'
                    }}
                  >
                    {collectionInput ? (
                      <>
                        {collectionInput}
                        {isNewCollection && (
                          <Typography
                            component="span"
                            sx={{
                              ml: 1,
                              fontSize: '12px',
                              color: theme.palette.primary.main
                            }}
                          >
                            + New
                          </Typography>
                        )}
                      </>
                    ) : (
                      'No folder'
                    )}
                  </Typography>
                </Button>

                {/* Collection Menu */}
                <Menu
                  anchorEl={collectionMenuAnchor}
                  open={collectionMenuOpen}
                  onClose={handleCollectionMenuClose}
                  PaperProps={{
                    sx: {
                      maxHeight: isMobile ? '60vh' : 440,
                      width: isMobile
                        ? '95vw'
                        : collectionMenuAnchor
                        ? `${collectionMenuAnchor.offsetWidth}px`
                        : '480px',
                      mt: 0.5,
                      borderRadius: '12px',
                      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
                      boxShadow: theme.palette.mode === 'dark'
                        ? '0 10px 40px -10px rgba(0, 0, 0, 0.6), 0 0 1px rgba(255, 255, 255, 0.1)'
                        : '0 10px 40px -10px rgba(0, 0, 0, 0.12), 0 0 1px rgba(0, 0, 0, 0.05)',
                      overflow: 'hidden'
                    }
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left'
                  }}
                >
                  {/* Search/Input box - Sticky */}
                  <Box
                    sx={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      backgroundColor: theme.palette.background.paper,
                      px: 2,
                      pt: 2,
                      pb: 1.5,
                      borderBottom: `1px solid ${theme.palette.divider}`
                    }}
                  >
                    <TextField
                      placeholder="Type to search or create new..."
                      value={collectionSearchTerm}
                      onChange={(e) => setCollectionSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && collectionSearchTerm.trim()) {
                          handleCollectionInputChange(
                            collectionSearchTerm.trim()
                          );
                        }
                      }}
                      autoFocus
                      InputProps={{
                        startAdornment: (
                          <Search
                            size={18}
                            style={{
                              marginRight: 10,
                              color: theme.palette.text.disabled
                            }}
                          />
                        )
                      }}
                      sx={{
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
                          fontSize: '14px',
                          transition: 'all 0.15s ease',
                          '& fieldset': {
                            border: 'none'
                          },
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.1)'
                          },
                          '&.Mui-focused': {
                            backgroundColor: theme.palette.background.paper,
                            borderColor: theme.palette.primary.main,
                            boxShadow: `0 0 0 3px ${theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(59, 130, 246, 0.1)'}`
                          }
                        },
                        '& .MuiOutlinedInput-input': {
                          fontSize: '14px',
                          py: 1,
                          px: 1.5,
                          '&::placeholder': {
                            color: theme.palette.text.disabled,
                            opacity: 1
                          }
                        }
                      }}
                    />
                  </Box>

                  {/* Options - Scrollable */}
                  <Box
                    sx={{
                      px: 2,
                      py: 1.5,
                      maxHeight: isMobile ? 'calc(60vh - 120px)' : 320,
                      overflowY: 'auto',
                      '&::-webkit-scrollbar': {
                        width: '8px'
                      },
                      '&::-webkit-scrollbar-track': {
                        background: 'transparent'
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                        borderRadius: '4px',
                        '&:hover': {
                          background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'
                        }
                      }
                    }}
                  >
                    {/* No Collection Option */}
                    <Box
                      onClick={() => handleCollectionInputChange('')}
                      sx={{
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                        borderRadius: '8px',
                        p: 1.5,
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
                          color: theme.palette.text.disabled,
                          borderRadius: '6px',
                          flexShrink: 0
                        }}
                      >
                        <X size={16} />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: '14px',
                          color: theme.palette.text.primary,
                          flex: 1
                        }}
                      >
                        No Folder
                      </Typography>
                      {!collectionInput && (
                        <Check
                          size={18}
                          weight="bold"
                          color={theme.palette.text.primary}
                        />
                      )}
                    </Box>

                    {/* Create new option if search term doesn't match any existing */}
                    {collectionSearchTerm.trim() &&
                      !collections.some(
                        (c) =>
                          c.title.toLowerCase() ===
                          collectionSearchTerm.trim().toLowerCase()
                      ) && (
                        <Box
                          onClick={() =>
                            handleCollectionInputChange(
                              collectionSearchTerm.trim()
                            )
                          }
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.12)' : 'rgba(59, 130, 246, 0.08)',
                            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.4)' : 'rgba(59, 130, 246, 0.3)'}`,
                            borderRadius: '8px',
                            p: 1.5,
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            transition: 'all 0.15s ease',
                            '&:hover': {
                              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.18)' : 'rgba(59, 130, 246, 0.12)',
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(59, 130, 246, 0.5)' : 'rgba(59, 130, 246, 0.4)'
                            }
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: '#3b82f6',
                              color: 'white',
                              borderRadius: '6px',
                              flexShrink: 0
                            }}
                          >
                            <AddIcon sx={{ fontSize: 16 }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                fontSize: '14px',
                                color: theme.palette.text.primary,
                                mb: 0.25
                              }}
                            >
                              Create "{collectionSearchTerm.trim()}"
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '12px',
                                color: theme.palette.text.secondary
                              }}
                            >
                              Press Enter or click to create
                            </Typography>
                          </Box>
                        </Box>
                      )}

                    {/* Existing collections */}
                    {filteredCollections.length > 0 ? (
                      <Stack spacing={0.5}>
                        {filteredCollections.map((collection) => {
                          const isSelected =
                            collectionInput === collection.title;
                          return (
                            <Box
                              key={collection.id}
                              onClick={() =>
                                handleCollectionInputChange(
                                  collection.title,
                                  collection.id
                                )
                              }
                              sx={{
                                cursor: 'pointer',
                                backgroundColor: 'transparent',
                                border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)'}`,
                                borderRadius: '8px',
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                transition: 'all 0.15s ease',
                                '&:hover': {
                                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  bgcolor: '#eff6ff',
                                  color: '#3b82f6',
                                  borderRadius: '6px',
                                  flexShrink: 0
                                }}
                              >
                                <Folders size={16} weight="regular" />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    color: theme.palette.text.primary,
                                    mb: 0.25
                                  }}
                                >
                                  {collection.title}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    fontSize: '12px'
                                  }}
                                >
                                  {collection.sessionCount} session
                                  {collection.sessionCount !== 1 ? 's' : ''}
                                </Typography>
                              </Box>
                              {isSelected && (
                                <Check
                                  size={18}
                                  weight="bold"
                                  color={theme.palette.text.primary}
                                />
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    ) : collectionSearchTerm &&
                      !collectionSearchTerm.trim() ? null : (
                      !collectionSearchTerm && (
                        <Box sx={{ py: 6, px: 3, textAlign: 'center' }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              mx: 'auto',
                              mb: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.02)',
                              borderRadius: '12px'
                            }}
                          >
                            <Folders size={24} weight="regular" color={theme.palette.text.disabled} />
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{ 
                              mb: 0.5,
                              fontWeight: 500,
                              fontSize: '14px',
                              color: theme.palette.text.primary
                            }}
                          >
                            No folders yet
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              fontSize: '13px',
                              color: theme.palette.text.secondary
                            }}
                          >
                            Type above to create one
                          </Typography>
                        </Box>
                      )
                    )}
                  </Box>
                </Menu>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Only show mode selection if mode prop is not provided (backward compatibility) */}
            {!mode && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    textAlign: 'left',
                    color: theme.palette.text.primary,
                    fontSize: '13px'
                  }}
                >
                  How would you like to create this session?
                </Typography>

                <ToggleButtonGroup
                  exclusive
                  color="primary"
                  value={creationMode}
                  onChange={handleCreationModeChange}
                  sx={{
                    mt: 1.5,
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                    gap: 1,
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: '8px',
                      borderColor: theme.palette.divider,
                      px: 2,
                      py: 1.5,
                      justifyContent: 'flex-start'
                    }
                  }}
                >
                  <ToggleButton value="record">Start a recording</ToggleButton>
                  <ToggleButton value="upload">Upload audio</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}


            {/* Summary Type Selection - Only show for multiple files */}
            {creationMode === 'upload' && selectedAudioFiles.length > 1 && (
              <Box
                onClick={() => setSummaryType(summaryType === 'individual' ? 'combined' : 'individual')}
                sx={{
                  mt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)'
                    }}
                  >
                    Combine summaries
                  </Typography>
                  <Tooltip 
                    title="When enabled, all files will be processed together into a single combined summary. When disabled, each file gets its own separate summary."
                    arrow
                    placement="top"
                  >
                    <Box
                      component="span"
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, 0.25)'}`,
                        fontSize: '10px',
                        fontWeight: 500,
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                        cursor: 'help',
                        '&:hover': {
                          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'
                        }
                      }}
                    >
                      i
                    </Box>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    width: 36,
                    height: 20,
                    borderRadius: '10px',
                    backgroundColor: summaryType === 'combined' 
                      ? (theme.palette.mode === 'dark' ? '#fff' : '#1A1A1A')
                      : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.15)'),
                    position: 'relative',
                    transition: 'all 0.2s ease',
                    flexShrink: 0
                  }}
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '8px',
                      backgroundColor: summaryType === 'combined'
                        ? (theme.palette.mode === 'dark' ? '#1A1A1A' : '#fff')
                        : '#fff',
                      position: 'absolute',
                      top: 2,
                      left: summaryType === 'combined' ? 18 : 2,
                      transition: 'all 0.2s ease',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.15)'
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Show selected files list at the bottom, before action buttons */}
            {creationMode === 'upload' && selectedAudioFiles.length > 0 && (
              <Box 
                sx={{ 
                  mt: 3, 
                  mb: 2,
                  transition: 'all 0.2s ease-out'
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    mb: 1.5,
                    fontSize: '13px',
                    color: theme.palette.text.primary,
                    transition: 'all 0.2s ease-out'
                  }}
                >
                  Selected Files ({selectedAudioFiles.length})
                </Typography>
                <Stack 
                  spacing={1}
                  sx={{
                    transition: 'all 0.2s ease-out'
                  }}
                >
                  {selectedAudioFiles.slice(0, showAllFiles ? selectedAudioFiles.length : (isMobile ? 2 : 3)).map((file, index) => (
                    <Card
                      key={`${file.name}-${index}`}
                      elevation={0}
                      sx={{
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px',
                        p: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        transition: 'all 0.2s ease-out',
                        animation: 'fadeIn 0.2s ease-out',
                        '@keyframes fadeIn': {
                          from: { opacity: 0, transform: 'translateY(-8px)' },
                          to: { opacity: 1, transform: 'translateY(0)' }
                        }
                      }}
                    >
                      {getFileIcon(file.name)}
                      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '13px'
                          }}
                        >
                          {file.name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            flexShrink: 0,
                            fontSize: '12px'
                          }}
                        >
                          • {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleClearSelectedAudio(index)}
                        sx={{ 
                          color: theme.palette.text.secondary,
                          width: 32,
                          height: 32,
                          padding: '4px'
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Card>
                  ))}
                  {((isMobile && selectedAudioFiles.length > 2) || (!isMobile && selectedAudioFiles.length > 3)) && !showAllFiles && (
                    <Typography
                      variant="body2"
                      onClick={() => setShowAllFiles(true)}
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: '13px',
                        textAlign: 'center',
                        py: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      + {selectedAudioFiles.length - (isMobile ? 2 : 3)} more
                    </Typography>
                  )}
                  {showAllFiles && selectedAudioFiles.length > (isMobile ? 2 : 3) && (
                    <Typography
                      variant="body2"
                      onClick={() => setShowAllFiles(false)}
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: '13px',
                        textAlign: 'center',
                        py: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Show less
                    </Typography>
                  )}
                </Stack>
              </Box>
            )}

            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                justifyContent: 'flex-end',
                mt: 3
              }}
            >
              <Button
                variant="outlined"
                onClick={() => {
                  setSessionTitle('');
                  setSearchTerm('');
                  setCollectionInput('');
                  setSelectedCollectionId(null);
                  setCollectionSearchTerm('');
                  // Don't change creation mode - keep current mode
                  handleClearSelectedAudio();
                  setUploadError(null);
                  setFileValidationError(null);
                  setShowAllFiles(false);

                  // Reset to default template
                  if (company && company.defaultTemplateId) {
                    const defaultTemplate = templates.find(
                      (t) => t.id === company.defaultTemplateId
                    );
                    if (defaultTemplate) {
                      setSelectedOption({
                        kind: 'template',
                        id: defaultTemplate.id,
                        name: defaultTemplate.name,
                        category: defaultTemplate.category?.name || 'General',
                        description:
                          defaultTemplate.description ||
                          'No description available',
                        questionCount: defaultTemplate.questions?.length || 0
                      });
                    }
                  }
                }}
                disabled={creating}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '6px',
                  py: 0.625,
                  px: 1.5,
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)'
                  }
                }}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                onClick={handlePrimaryAction}
                disabled={isPrimaryActionDisabled}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 600,
                  borderRadius: '6px',
                  py: 0.625,
                  px: 2,
                  backgroundColor: theme.palette.mode === 'dark' ? '#fff' : '#1A1A1A',
                  color: theme.palette.mode === 'dark' ? '#000' : '#fff',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#000',
                    boxShadow: 'none'
                  },
                  '&:disabled': {
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
                    boxShadow: 'none'
                  }
                }}
              >
                {primaryActionLabel}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={uploadDialogOpen}
        onClose={handleUploadDialogClose}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown={isUploadingFile}
      >
        <DialogTitle>Uploading files</DialogTitle>
        <DialogContent>
          {(uploadFileStatuses.length > 0 || selectedAudioFiles.length > 0) && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              {(uploadFileStatuses.length || selectedAudioFiles.length)} file
              {(uploadFileStatuses.length || selectedAudioFiles.length) > 1
                ? 's'
                : ''}{' '}
              selected
            </Typography>
          )}
          <LinearProgress
            variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
            value={uploadProgress}
            sx={{ mb: 2 }}
          />
          <Typography variant="body2" color="text.secondary">
            {uploadStatusText}
          </Typography>
          {uploadFileStatuses.length > 0 && (
            <Stack
              spacing={1}
              sx={{
                mt: 2,
                maxHeight: 280,
                overflowY: 'auto',
                pr: 0.5
              }}
            >
              {uploadFileStatuses.map((fileStatus) => (
                <Box
                  key={fileStatus.id}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                    p: 1.25,
                    bgcolor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.03)'
                        : 'rgba(0, 0, 0, 0.015)'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minWidth: 0
                    }}
                  >
                    {getFileIcon(fileStatus.name)}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {fileStatus.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatFileSize(fileStatus.size)}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={getUploadStatusLabel(fileStatus.status)}
                      sx={{
                        height: 22,
                        fontSize: '11px',
                        bgcolor:
                          fileStatus.status === 'complete'
                            ? theme.palette.success.light
                            : fileStatus.status === 'failed'
                            ? theme.palette.error.light
                            : fileStatus.status === 'retrying'
                            ? theme.palette.warning.light
                            : theme.palette.action.hover,
                        color:
                          fileStatus.status === 'complete'
                            ? theme.palette.success.contrastText
                            : fileStatus.status === 'failed'
                            ? theme.palette.error.contrastText
                            : fileStatus.status === 'retrying'
                            ? theme.palette.warning.contrastText
                            : theme.palette.text.secondary
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: 38,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums'
                      }}
                    >
                      {Math.round(fileStatus.progress)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(fileStatus.progress, 100)}
                    sx={{
                      mt: 1,
                      height: 5,
                      borderRadius: 999,
                      bgcolor: theme.palette.action.hover,
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 999,
                        bgcolor:
                          fileStatus.status === 'failed'
                            ? theme.palette.error.main
                            : fileStatus.status === 'complete'
                            ? theme.palette.success.main
                            : theme.palette.primary.main
                      }
                    }}
                  />
                  {fileStatus.message && (
                    <Typography
                      variant="caption"
                      color={
                        fileStatus.status === 'failed'
                          ? 'error'
                          : 'text.secondary'
                      }
                      sx={{
                        display: 'block',
                        mt: 0.75,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {fileStatus.message}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {isUploadingFile ? (
            <Button onClick={handleCancelUpload} color="inherit">
              Cancel upload
            </Button>
          ) : (
            <Button onClick={handleUploadDialogClose}>Close</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Template Preview Dialog */}
      {previewTemplateId && (
        <Dialog
          open={!!previewTemplateId}
          onClose={handleClosePreview}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          PaperProps={{
            sx: {
              borderRadius: isMobile ? 0 : 2,
              maxHeight: isMobile ? '100vh' : '80vh'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Template Preview
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {templates.find((t) => t.id === previewTemplateId)?.name ||
                    'Template'}
                </Typography>
              </Box>
              <IconButton onClick={handleClosePreview} size="small">
                <X size={20} />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {(() => {
              const template = templates.find(
                (t) => t.id === previewTemplateId
              );
              if (!template) {
                return (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      Template not found
                    </Typography>
                  </Box>
                );
              }

              const questions = template.questions || [];

              if (questions.length === 0) {
                return (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <HelpCircle
                      size={48}
                      style={{
                        color: theme.palette.text.secondary,
                        marginBottom: 16
                      }}
                    />
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      No questions yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      This template doesn't have any questions configured.
                    </Typography>
                  </Box>
                );
              }

              return (
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      This template contains {questions.length} question
                      {questions.length !== 1 ? 's' : ''}:
                    </Typography>
                  </Box>
                  <List sx={{ p: 0 }}>
                    {questions.map((question, index) => (
                      <ListItem
                        key={question.id || index}
                        sx={{
                          px: 0,
                          py: 1,
                          borderBottom:
                            index < questions.length - 1
                              ? '1px solid #f3f4f6'
                              : 'none'
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  bgcolor:
                                    theme.palette.mode === 'dark'
                                      ? 'rgba(255, 255, 255, 0.08)'
                                      : '#f3f4f6',
                                  color: theme.palette.text.secondary,
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontWeight: 500,
                                  minWidth: 24,
                                  textAlign: 'center',
                                  flexShrink: 0
                                }}
                              >
                                {index + 1}
                              </Typography>
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {question.title || 'Untitled question'}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            question.description && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 4 }}
                              >
                                {question.description}
                              </Typography>
                            )
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={handleClosePreview} variant="outlined">
              Close
            </Button>
            <Button
              onClick={() => {
                const template = templates.find(
                  (t) => t.id === previewTemplateId
                );
                if (template) {
                  handleOptionSelect({
                    kind: 'template',
                    id: template.id,
                    name: template.name,
                    category: template.category?.name || 'General',
                    description:
                      template.description || 'No description available',
                    questionCount: template.questions?.length || 0
                  });
                  handleClosePreview();
                }
              }}
              variant="contained"
              sx={{
                backgroundColor:
                  theme.palette.mode === 'dark' ? '#1A1A1A' : '#1f2937',
                '&:hover': { backgroundColor: '#000' }
              }}
            >
              Use This Template
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B';
  }
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024))
  );
  const value = bytes / Math.pow(1024, exponent);
  const formatted =
    value >= 10 || exponent === 0 ? value.toFixed(0) : value.toFixed(1);
  return `${formatted} ${units[exponent]}`;
};

export default DashboardCreateSessionPage;
